import { useSelector } from "react-redux";
import { isFeatureEnabled } from "@redux/selectors";
import { selectOrganizationFlags } from "@redux/selectors/appConfigurations";
import { Permissions, useAuthorizationContext } from "contexts/AuthorizationContext";
import { isFlagOnFor } from "contexts/orgFilter";

// An API type is available when the user is super admin, or the organization
// has the feature flag on and the user holds the matching read permission.
const isApiTypeAvailable = (isSuperAdmin, orgGuid, featureEnabled, hasReadPermission) =>
  isSuperAdmin || Boolean(orgGuid && featureEnabled && hasReadPermission);

// Retrieve the product a customer is associated to, based on the enabled
// PublicApi.* feature flag. Standard has precedence over Tier2, which has
// precedence over EuDataAct, when more than one is enabled (e.g. super
// admin, who has every feature flag turned on).
export const getProductName = (isDataActEnabled, isStandardEnabled, isTier2Enabled) => {
  if (isStandardEnabled) return "standard";
  if (isTier2Enabled) return "tier2";
  if (isDataActEnabled) return "eudataact";
  return null;
};

const MQTT_API_FLAG = "MqttApi";
const PUBLIC_API_FLAGS = ["PublicApi.EuDataAct", "PublicApi.Standard", "PublicApi.Tier2"];
const API_FLAGS = [MQTT_API_FLAG, ...PUBLIC_API_FLAGS];

// The rule for one organization, given its flags (name -> boolean).
const computeApiAccess = ({ isSuperAdmin, orgGuid, flags, hasPermission }) => {
  const [isDataActEnabled, isStandardEnabled, isTier2Enabled] = PUBLIC_API_FLAGS.map(
    (name) => !!flags[name]
  );
  const mqttAvailable = isApiTypeAvailable(
    isSuperAdmin,
    orgGuid,
    !!flags[MQTT_API_FLAG],
    hasPermission(orgGuid, Permissions.MqttRead),
  );
  const restAvailable = isApiTypeAvailable(
    isSuperAdmin,
    orgGuid,
    isDataActEnabled || isStandardEnabled || isTier2Enabled,
    hasPermission(orgGuid, Permissions.OrganizationWrite),
  );
  return {
    mqttAvailable,
    restAvailable,
    // The API page/menu is visible when at least one API type is available.
    canSeeApi: mqttAvailable || restAvailable,
    // The single Product the customer is associated to (from the PublicApi.*
    // flag), used by the API Keys UI. null when no REST product is enabled.
    productName: getProductName(isDataActEnabled, isStandardEnabled, isTier2Enabled),
  };
};

const organizationApiFlags = (flagsByOrg, orgGuid) =>
  Object.fromEntries(API_FLAGS.map((name) => [name, isFlagOnFor(flagsByOrg, orgGuid, name)]));

/**
 * Single source of truth for API-page access (MQTT + RESTful).
 *
 * Centralizes the feature-flag names, the read-permission mapping and the
 * super-admin bypass so the rule is defined once and reused by the API page,
 * the sidebar menu and the route guard.
 *
 * With the organization filter the store's flags are those of the scope ("on
 * for any" with no filter), so the evaluated organization's own flags are
 * read instead: the flag and the permission then belong to the same one.
 *
 * @param {string} [orgGuid] Organization to evaluate. Defaults to the active
 *   organization from the authorization context when omitted.
 * @returns {{ mqttAvailable: boolean, restAvailable: boolean, canSeeApi: boolean }}
 */
export const useApiAccess = (orgGuid) => {
  const { isSuperAdmin, hasPermission, activeOrganization, isOrgFilterEnabled } =
    useAuthorizationContext();
  const storeFlags = {
    MqttApi: useSelector(isFeatureEnabled("MqttApi")),
    "PublicApi.EuDataAct": useSelector(isFeatureEnabled("PublicApi.EuDataAct")),
    "PublicApi.Standard": useSelector(isFeatureEnabled("PublicApi.Standard")),
    "PublicApi.Tier2": useSelector(isFeatureEnabled("PublicApi.Tier2")),
  };
  const flagsByOrg = useSelector(selectOrganizationFlags);

  const guid = orgGuid !== undefined ? orgGuid : activeOrganization?.guid ?? null;
  // A super admin has no per-organization flags: theirs are forced on.
  const flags =
    isOrgFilterEnabled && !isSuperAdmin() ? organizationApiFlags(flagsByOrg, guid) : storeFlags;

  return computeApiAccess({ isSuperAdmin: isSuperAdmin(), orgGuid: guid, flags, hasPermission });
};

// The user's organizations where the API page can be used, for the
// organization filter (whatever the filter). A super admin can use it anywhere.
// `organizationsOf`: which of the context's organizations to look at (every
// one by default).
export const useAnyOrganizationApiAccess = (organizationsOf = (context) => context.organizations) => {
  const context = useAuthorizationContext();
  const { isSuperAdmin, hasPermission } = context;
  const organizations = organizationsOf(context) ?? [];
  const flagsByOrg = useSelector(selectOrganizationFlags);
  // Every one, without evaluating hundreds of customers on each render.
  if (isSuperAdmin()) return { canSeeApi: true, usableOrganizations: organizations };
  const usableOrganizations = organizations.filter(
    (org) =>
      computeApiAccess({
        isSuperAdmin: false,
        orgGuid: org.guid,
        flags: organizationApiFlags(flagsByOrg, org.guid),
        hasPermission,
      }).canSeeApi
  );
  return { canSeeApi: usableOrganizations.length > 0, usableOrganizations };
};

// The sidebar entry and the route: the active organization's access, or with
// the organization filter, whether an organization in scope allows the page
// (the filtered one, or any with no filter).
const inScope = (context) => context.organizationsInScope;

export const useCanSeeApiPage = () => {
  const { isOrgFilterEnabled } = useAuthorizationContext();
  const activeOrganizationAccess = useApiAccess();
  const anyOrganizationAccess = useAnyOrganizationApiAccess(inScope);
  return isOrgFilterEnabled ? anyOrganizationAccess.canSeeApi : activeOrganizationAccess.canSeeApi;
};

export default useApiAccess;
